/**
 * Utility function to evaluate bacteria conditions with proper negative value handling
 * This function wraps the original conditions to handle edge cases like negative values
 */

/**
 * Evaluates a bacteria condition with special handling for negative values
 * @param {Function} condition - The original condition function from bacteriaMetaConfig
 * @param {number} x - The bacteria CT value
 * @param {Object} formData - The form data containing Universal bacteria value
 * @param {string} bacteriaName - The name of the bacteria being evaluated
 * @returns {boolean} - Whether the condition should match
 */
export function evaluateBacteriaCondition(condition, x, formData, bacteriaName) {
  if (typeof condition !== 'function') {
    return false;
  }

  // Ensure x is a proper number (handles string values from CSV/form)
  const numericX = Number(x);
  
  if (isNaN(numericX)) {
    console.warn(`Invalid value for ${bacteriaName}:`, x);
    return false;
  }

  // Normalize formData so conditions that use Universal bacteria get a number
  const normalizedFormData = { ...formData };
  if (formData && formData['Universal bacteria'] != null && formData['Universal bacteria'] !== '') {
    normalizedFormData['Universal bacteria'] = Number(formData['Universal bacteria']);
  }

  // All panels use conditions from calculate_ctvalue.js (single source of truth)
  return condition(numericX, normalizedFormData);
}

/**
 * Gets the appropriate score and label for a bacteria based on its value
 * @param {string} bacteriaName - The name of the bacteria
 * @param {number} value - The CT value
 * @param {Object} formData - The form data
 * @param {Object} bacteriaConfig - The bacteria configuration from bacteriaMetaConfig
 * @returns {Object} - Object containing score, label, and color
 */
export function getBacteriaScore(bacteriaName, value, formData, bacteriaConfig) {
  if (!bacteriaConfig || !Array.isArray(bacteriaConfig.rangeMapping)) {
    return { score: 3, label: 'Normal', color: '#00B050' };
  }

  // Find the first matching condition
  for (const range of bacteriaConfig.rangeMapping) {
    if (evaluateBacteriaCondition(range.condition, value, formData, bacteriaName)) {
      return {
        score: range.score,
        label: range.label,
        color: range.color,
        score_cal: range.score_cal
      };
    }
  }

  // Default fallback
  return { score: 3, label: 'Normal', color: '#00B050' };
}

/**
 * Preprocesses a value to handle negative values properly
 * This function can be used to modify values before they reach the conditions
 * @param {number} value - The original value
 * @param {string} bacteriaName - The name of the bacteria
 * @returns {number} - The processed value
 */
export function preprocessBacteriaValue(value, bacteriaName) {
  // For ALL bacteria, ensure proper number conversion
  const numericValue = Number(value);
  
  if (isNaN(numericValue)) {
    console.warn(`Invalid value for ${bacteriaName}:`, value);
    return 0; // Default fallback
  }
  
  return numericValue;
}

/**
 * Test function to verify negative value handling
 * This helps debug if negative values are working correctly
 */
export function testNegativeValueHandling() {
  console.log('=== Testing Negative Value Handling ===');
  
  // Test basic comparisons
  console.log('x = -10, x <= 35:', -10 <= 35); // Should be true
  console.log('x = -5, x < 35:', -5 < 35); // Should be true
  console.log('x = -2, x <= 12.28:', -2 <= 12.28); // Should be true
  
  // Test with formData
  const testFormData = { 'Universal bacteria': 35 };
  const testX = -10;
  const difference = testX - testFormData['Universal bacteria'];
  console.log('x = -10, Universal bacteria = 35, difference =', difference);
  console.log('difference < 20:', difference < 20); // Should be true (-45 < 20)
  
  // Test your specific case
  const yourFormData = { 'Universal bacteria': 20 };
  const yourX = 17.6;
  const yourDifference = yourX - yourFormData['Universal bacteria'];
  console.log('=== Your Test Case 1 ===');
  console.log('x = 17.6, Universal bacteria = 20, difference =', yourDifference);
  console.log('17.6 <= 12.28:', 17.6 <= 12.28); // Should be false
  console.log('17.6 > 12.28 && 17.6 < 35:', 17.6 > 12.28 && 17.6 < 35); // Should be true
  console.log('difference <= 13.28:', yourDifference <= 13.28); // Should be true (-2.4 <= 13.28)
  
  // Test your new case
  const yourNewFormData = { 'Universal bacteria': 2 };
  const yourNewX = 17.6;
  const yourNewDifference = yourNewX - yourNewFormData['Universal bacteria'];
  console.log('=== Your Test Case 2 ===');
  console.log('x = 17.6, Universal bacteria = 2, difference =', yourNewDifference);
  console.log('17.6 <= 12.28:', 17.6 <= 12.28); // Should be false
  console.log('17.6 > 12.28 && 17.6 < 35:', 17.6 > 12.28 && 17.6 < 35); // Should be true
  console.log('difference <= 13.28:', yourNewDifference <= 13.28); // Should be false (15.6 > 13.28)
  console.log('17.6 >= 35:', 17.6 >= 35); // Should be false
  console.log('difference > 13.28:', yourNewDifference > 13.28); // Should be true (15.6 > 13.28)
  console.log('Third condition should match:', 17.6 >= 35 || yourNewDifference > 13.28); // Should be true
  
  console.log('=== End Test ===');
}
